import React, { useState, useRef, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Sparkles, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ChatWidget = () => {
  const { chatOpen, setChatOpen } = useApp();
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Welcome to MOVA. I'm your personal concierge — ask me about travel, fashion, self-care or planning your week." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, chatOpen]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const next = [...messages, { role: "user", content: input.trim() }];
    setMessages(next);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/chat", { messages: next.map((m) => ({ role: m.role, content: m.content })) });
      setMessages([...next, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages([...next, { role: "assistant", content: "I'm momentarily away. Please try again shortly." }]);
    } finally {
      setSending(false);
    }
  };

  const sendContact = async () => {
    if (!contact.name || !contact.email || !contact.message) {
      toast.error("Please fill in all fields");
      return;
    }
    try {
      await api.post("/contact", contact);
      toast.success("Message sent to our concierge team");
      setContact({ name: "", email: "", message: "" });
      setTab("chat");
    } catch (e) {
      toast.error("Could not send message");
    }
  };

  return (
    <>
      <button
        onClick={() => setChatOpen(!chatOpen)}
        data-testid="chat-widget-toggle"
        className="fixed bottom-6 right-6 z-[60] w-15 h-15 p-4 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] shadow-2xl gold-glow hover:scale-105 transition-transform duration-200"
        aria-label="Open concierge chat"
      >
        {chatOpen ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {chatOpen && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-3xl glass border border-[rgba(212,175,55,0.25)] flex flex-col overflow-hidden shadow-2xl animate-fade-up" data-testid="chat-widget-panel">
          <div className="p-4 border-b border-[rgba(212,175,55,0.15)] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border border-amber-400/40 flex items-center justify-center text-champagne"><Sparkles size={16} /></div>
            <div className="flex-1">
              <p className="text-sm font-medium text-champagne">MOVA Concierge</p>
              <p className="text-[10px] text-amber-400/70">Online · replies instantly</p>
            </div>
          </div>

          <div className="flex border-b border-white/5 text-sm">
            <button onClick={() => setTab("chat")} data-testid="chat-tab-ai" className={`flex-1 py-2.5 transition-colors ${tab === "chat" ? "text-champagne border-b-2 border-amber-400" : "text-slate-400"}`}>AI Chat</button>
            <button onClick={() => setTab("contact")} data-testid="chat-tab-contact" className={`flex-1 py-2.5 transition-colors ${tab === "contact" ? "text-champagne border-b-2 border-amber-400" : "text-slate-400"}`}>Message Admin</button>
          </div>

          {tab === "chat" ? (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12]" : "bg-[#17171f] text-slate-200 border border-white/5"}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && <div className="flex justify-start"><div className="px-4 py-2.5 rounded-2xl bg-[#17171f] border border-white/5"><Loader2 className="animate-spin text-champagne" size={16} /></div></div>}
              </div>
              <div className="p-3 border-t border-white/5 flex gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask the concierge…"
                  data-testid="chat-input"
                  className="flex-1 bg-[#14141c] border border-amber-400/15 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/40"
                />
                <button onClick={send} data-testid="chat-send-btn" disabled={sending} className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] flex items-center justify-center shrink-0 disabled:opacity-50">
                  <Send size={16} />
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="contact-form">
              <p className="text-sm text-slate-400">Leave a message and our concierge team will reach out.</p>
              <input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} placeholder="Your name" data-testid="contact-name" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
              <input value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" data-testid="contact-email" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none" />
              <textarea value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} placeholder="How can we help?" rows={4} data-testid="contact-message" className="w-full bg-[#14141c] border border-amber-400/15 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none resize-none" />
              <button onClick={sendContact} data-testid="contact-send-btn" className="w-full flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#F3E5AB] to-[#D4AF37] text-[#1a1a12] py-3 font-medium hover:opacity-90 transition-opacity">
                <Mail size={16} /> Send message
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};
